// @intent: Pure drag interpretation for native match-3 input.
// @flow: pointerdown design point -> locked cell -> pointerup dominant-axis delta -> adjacent swap.
// @stale: Do not switch release handling to hit-test-by-release-pixel; that caused bad swipe coordinates in PB2.

import type { Match3CellRect } from './Match3Layout.js';

export type Match3SwipeDirection = 'left' | 'right' | 'up' | 'down';

export interface Match3Point {
  readonly x: number;
  readonly y: number;
}

export interface Match3DragState {
  readonly fromIndex: number;
  readonly start: Match3Point;
}

export interface BeginMatch3DragInput {
  readonly point: Match3Point;
  readonly cells: readonly Match3CellRect[];
  readonly isSwappableIndex: (index: number) => boolean;
}

export interface FinishMatch3DragInput {
  readonly drag: Match3DragState | null;
  readonly point: Match3Point;
  readonly rows: number;
  readonly cols: number;
  readonly minDistance?: number;
}

export interface Match3ResolvedDrag {
  readonly fromIndex: number;
  readonly toIndex: number;
  readonly direction: Match3SwipeDirection;
}

function pointInCell(point: Match3Point, cell: Match3CellRect): boolean {
  return point.x >= cell.x && point.x <= cell.x + cell.width && point.y >= cell.y && point.y <= cell.y + cell.height;
}

function isValidGrid(rows: number, cols: number): boolean {
  return Number.isInteger(rows) && rows > 0 && Number.isInteger(cols) && cols > 0;
}

export function beginMatch3Drag(input: BeginMatch3DragInput): Match3DragState | null {
  const cell = input.cells.find((candidate) => pointInCell(input.point, candidate));
  if (!cell) return null;
  if (!input.isSwappableIndex(cell.tileIndex)) return null;
  return {
    fromIndex: cell.tileIndex,
    start: { x: input.point.x, y: input.point.y },
  };
}

export function finishMatch3Drag(input: FinishMatch3DragInput): Match3ResolvedDrag | null {
  if (!input.drag || !isValidGrid(input.rows, input.cols)) return null;
  const dx = input.point.x - input.drag.start.x;
  const dy = input.point.y - input.drag.start.y;
  const distance = Math.max(Math.abs(dx), Math.abs(dy));
  if (distance < (input.minDistance ?? 18)) return null;

  const fromRow = Math.floor(input.drag.fromIndex / input.cols);
  const fromCol = input.drag.fromIndex % input.cols;
  let toRow = fromRow;
  let toCol = fromCol;
  let direction: Match3SwipeDirection;

  if (Math.abs(dx) >= Math.abs(dy)) {
    if (dx >= 0) {
      direction = 'right';
      toCol += 1;
    } else {
      direction = 'left';
      toCol -= 1;
    }
  } else if (dy >= 0) {
    direction = 'down';
    toRow += 1;
  } else {
    direction = 'up';
    toRow -= 1;
  }

  if (toRow < 0 || toRow >= input.rows || toCol < 0 || toCol >= input.cols) return null;

  return {
    fromIndex: input.drag.fromIndex,
    toIndex: toRow * input.cols + toCol,
    direction,
  };
}
